/**
 * The player's own voice: the taunt tray, and Show.
 *
 * ============================ WHAT THIS IS =====================================
 * Two controls at the edge of the action band, and both of them are one tap.
 *
 * THE TRAY. A button at the band's edge opens a ONE-ROW sheet: the stickers if any have
 * landed, then the emoji row, then three lines. One row and no picker, because the
 * clock is running: a full emoji picker at a poker table is a way to spend eight
 * seconds of a ten-second decision choosing a face. The button draws its own cooldown —
 * twenty seconds, as a ring that empties — so a player who cannot taunt can see why
 * rather than pressing a dead button.
 *
 * SHOW. After an uncontested win or after folding, the hand the player was not made to
 * reveal is theirs to reveal, one card or both, for three seconds. It appears in the
 * band only while the offer stands and disappears with it.
 *
 * ## Accessibility, and why it is not an afterthought here
 *
 * Everything is a real `<button>` in DOM order, so Tab reaches it; Escape closes the
 * sheet and returns focus to the tray button; every target is at least 44px, which on
 * a phone is the difference between a control and a decoration. The sheet is a
 * `role="dialog"` with a label, and each taunt's button carries its own label rather
 * than an emoji a screen reader would read as "grinning face with smiling eyes".
 * ===============================================================================
 */
import { useEffect, useRef, useState } from "react";
import { EMOJI_ROW, SEEDED_TAUNTS, type TauntItem } from "./lines";
import { stickersNow, loadStickers, stickerUrl } from "../art/stickers";

export interface TauntTrayProps {
  /** Whether the tray exists at all. Table talk off puts it away. */
  enabled: boolean;
  /** Milliseconds until the next taunt is allowed. 0 means now. */
  cooldownLeft: number;
  /** The whole cooldown, so the ring knows what fraction is left. */
  cooldownTotal: number;
  /** Send one. Returns false if the cooldown beat the player to it. */
  onTaunt: (id: string) => boolean;
}

export function TauntTray({ enabled, cooldownLeft, cooldownTotal, onTaunt }: TauntTrayProps) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  /* The sticker manifest is empty today and is fetched once; when Tim's renders land
     the row grows and nothing else changes. */
  const [stickers, setStickers] = useState(() => stickersNow());
  useEffect(() => { void loadStickers().then((m) => setStickers(m.stickers)); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); btn.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    /* First thing in the row takes focus, so the keyboard path is one Tab shorter. */
    sheet.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!enabled) return null;

  const ready = cooldownLeft <= 0;
  const left = Math.ceil(cooldownLeft / 1000);
  const frac = cooldownTotal > 0 ? Math.max(0, Math.min(1, cooldownLeft / cooldownTotal)) : 0;

  /* Stickers first if they exist, then the eight emoji, then the three lines. */
  const items: TauntItem[] = [
    ...stickers.map((s) => ({ id: s.id, kind: "sticker" as const, label: s.label, text: "", sticker: s.id })),
    ...EMOJI_ROW,
    ...SEEDED_TAUNTS,
  ];

  const send = (id: string) => {
    if (onTaunt(id)) setOpen(false);
    btn.current?.focus();
  };

  return (
    <div className="td-tray">
      <button ref={btn} type="button" className="td-tray-btn" data-td="tray"
              aria-expanded={open} aria-haspopup="dialog"
              aria-label={ready ? "Say something" : `Say something — ${left} seconds`}
              disabled={!ready}
              style={{ "--cd": frac } as React.CSSProperties}
              onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">{ready ? "\u{1F5E8}" : left}</span>
      </button>

      {open && (
        <div ref={sheet} className="td-traysheet" role="dialog" aria-label="Say something">
          {items.map((t) => (
            <button key={t.id} type="button" className={`td-trayitem td-trayitem-${t.kind}`}
                    aria-label={t.label} title={t.label} onClick={() => send(t.id)}>
              {t.kind === "sticker" && stickerUrl(t.sticker)
                ? <img src={stickerUrl(t.sticker)!} alt="" />
                : <span aria-hidden="true">{t.kind === "emoji" ? t.text : t.label}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Show --- */

export interface ShowControlProps {
  /** The hero's two cards, from `Game.liveHoles` — null while the hand is live. */
  cards: readonly number[] | null;
  /** Show these. One index, or both. */
  onShow: (cards: readonly number[]) => void;
  /** Nothing left to offer: the hero showed, or the offer timed out. */
  done: boolean;
}

/**
 * Show — one card or both.
 *
 * Three buttons rather than a menu, because "which card" is the whole decision and
 * hiding it behind a second tap turns a flourish into paperwork. The FIRST and SECOND
 * are named by their position in the hand as the player sees it, left and right, which
 * is how anybody would describe the card they are turning over.
 */
export function ShowControl({ cards, onShow, done }: ShowControlProps) {
  if (done || !cards || cards.length !== 2) return null;
  return (
    <div className="td-show" data-td="show" role="group" aria-label="Show your hand">
      <span className="td-show-label">Show</span>
      <button type="button" className="td-show-btn" onClick={() => onShow([cards[0]!])}>Left</button>
      <button type="button" className="td-show-btn" onClick={() => onShow([cards[1]!])}>Right</button>
      <button type="button" className="td-show-btn td-show-both" onClick={() => onShow(cards)}>Both</button>
    </div>
  );
}
